import React, { useState, useEffect, useContext } from 'react';
import {
    Package, Truck, AlertTriangle, Search, Filter,
    ArrowDownLeft, ArrowUpRight, Layers, MapPin, Plus,
    X, CheckCircle, Loader2, ClipboardCheck, Trash2, History, FileText
} from 'lucide-react';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const InventoryModule = () => {
    const { user } = useContext(AuthContext);
    // Data States
    const [items, setItems] = useState([]);
    const [projects, setProjects] = useState([]);
    const [logs, setLogs] = useState([]); // Used for GRN and Wastage views
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [activeTab, setActiveTab] = useState('stock'); // 'stock', 'grn', 'wastage'

    // UI States
    const [modalType, setModalType] = useState(null); // 'transfer', 'update', 'wastage'
    const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);

    // Form States
    const [formData, setFormData] = useState({
        inventory_id: '', project_id: '', quantity: '', type: 'in', notes: '', reason: 'Cutting Loss'
    });

    const [newItem, setNewItem] = useState({
        material_name: '', category: 'Plywood', unit: 'Sheets', low_stock_threshold: 10, initial_stock: 0
    });

    useEffect(() => { loadInitialData(); }, [activeTab]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            // Fetch basic lists
            const [invRes, projRes] = await Promise.all([
                api.get('get_inventory.php'),
                api.get('get_projects.php')
            ]);
            setItems(invRes.data.items || []);
            setProjects(projRes.data.projects || []);

            // If on GRN or Wastage tab, fetch logs
            if (activeTab !== 'stock') {
                const logRes = await api.get(`get_inventory_logs.php?type=${activeTab}`);
                setLogs(logRes.data.logs || []);
            }
        } catch (err) { console.error("Data Sync Failed", err); }
        finally { setLoading(false); }
    };

    const handleAction = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            let endpoint = 'update_stock_level.php';
            if (modalType === 'transfer') endpoint = 'transfer_stock.php';
            if (modalType === 'wastage') endpoint = 'record_wastage.php';

            const res = await api.post(endpoint, formData);
            if (res.data.status === 'success') {
                setModalType(null);
                setFormData({ inventory_id: '', project_id: '', quantity: '', type: 'in', notes: '', reason: 'Cutting Loss' });
                loadInitialData();
                alert("Inventory Updated Successfully");
            }
        } catch (err) { alert("Operation failed"); }
        finally { setFormLoading(false); }
    };

    const handleAddNewItem = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const res = await api.post('add_inventory_item.php', newItem);
            if (res.data.status === 'success') {
                setIsNewItemModalOpen(false);
                setNewItem({ material_name: '', category: 'Plywood', unit: 'Sheets', low_stock_threshold: 10, initial_stock: 0 });
                loadInitialData();
                alert("New material added to master list!");
            }
        } catch (err) { alert("Failed to add material"); }
        finally { setFormLoading(false); }
    };

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center text-slate-400 font-black italic uppercase tracking-widest">
            <Loader2 className="animate-spin mb-4 text-indigo-600" size={48} />
            Auditing Warehouse...
        </div>
    );

    const lowStockItems = items.filter(i => parseFloat(i.stock) <= parseFloat(i.low_stock_threshold));

    return (
        <div className="space-y-8 p-4 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">Inventory Control</h1>
                    <p className="text-slate-500 font-medium italic">{`${user.company_name} Warehouse Management`}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button onClick={() => { setModalType('transfer'); setFormData({ ...formData, type: 'transfer' }); }} className="bg-white border border-slate-200 px-6 py-4 rounded-2xl font-black text-[10px] uppercase text-slate-600 flex items-center gap-2 hover:bg-slate-50 transition-all shadow-sm"><Truck size={16} /> Site Transfer</button>
                    <button onClick={() => { setModalType('wastage'); }} className="bg-rose-50 text-rose-600 px-6 py-4 rounded-2xl font-black text-[10px] uppercase border border-rose-100 flex items-center gap-2 hover:bg-rose-600 hover:text-white transition-all shadow-sm"><Trash2 size={16} /> Log Wastage</button>
                    <button

                        onClick={() => { setModalType('update'); setFormData({ ...formData, type: 'in' }); }}

                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl shadow-indigo-100 transition-all flex items-center gap-2"

                    >

                        <Plus size={18} /> Stock In/Out

                    </button>
                    <button onClick={() => setIsNewItemModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl shadow-indigo-100 transition-all flex items-center gap-2"><Plus size={16} /> New Material</button>
                </div>
            </div>

            {/* Low Stock Strip */}
            {activeTab === 'stock' && lowStockItems.length > 0 && (
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                    {lowStockItems.map(item => (
                        <div key={item.id} className="flex-shrink-0 bg-amber-50 border border-amber-100 p-4 rounded-2xl flex items-center gap-4 animate-pulse shadow-sm">
                            <AlertTriangle className="text-amber-600" size={20} />
                            <div>
                                <p className="text-[9px] font-black text-amber-500 uppercase tracking-widest leading-none">Critical Stock</p>
                                <p className="text-xs font-bold text-slate-900 uppercase italic mt-1">{item.name}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="flex gap-4 bg-slate-100/50 p-2 rounded-2xl w-fit border border-slate-100">
                {['stock', 'grn', 'wastage'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-slate-900 text-white shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        {tab === 'stock' ? 'Warehouse Stock' : tab === 'grn' ? 'GRN Logs' : 'Wastage Report'}
                    </button>
                ))}
            </div>

            {/* --- TAB CONTENT: LIVE STOCK --- */}
            {activeTab === 'stock' && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4">
                    <div className="p-8 border-b border-slate-50 bg-slate-50/30">
                        <div className="flex items-center gap-4 max-w-md bg-white px-4 py-3 rounded-2xl border border-slate-100 shadow-inner">
                            <Search className="text-slate-300" size={18} />
                            <input type="text" placeholder="Filter materials..." className="bg-transparent outline-none font-bold text-sm text-slate-600 w-full" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <tr><th className="p-8">Material Identity</th><th className="p-8 text-center">In Stock</th><th className="p-8 text-right">Actions</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {items.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase())).map(item => (
                                <tr key={item.id} className="group hover:bg-slate-50/50 transition-all">
                                    <td className="p-8">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black group-hover:bg-indigo-600 group-hover:text-white transition-all">{item.cat[0]}</div>
                                            <div>
                                                <p className="font-black uppercase text-slate-900 italic tracking-tight">{item.name}</p>
                                                <p className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em]">{item.cat}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-8 text-center">
                                        <span className={`px-4 py-2 rounded-xl font-black text-sm ${parseFloat(item.stock) <= parseFloat(item.low_stock_threshold) ? 'bg-rose-50 text-rose-600 border border-rose-100' : 'bg-slate-100 text-slate-700'}`}>
                                            {item.stock} {item.unit}
                                        </span>
                                    </td>
                                    <td className="p-8 text-right">
                                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => { setSelectedItem(item); setModalType('update'); setFormData({ ...formData, inventory_id: item.id, type: 'in' }); }} className="p-3 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-600 hover:text-white transition-all"><Plus size={16} /></button>
                                            <button onClick={() => { setSelectedItem(item); setModalType('transfer'); setFormData({ ...formData, inventory_id: item.id, type: 'transfer' }); }} className="p-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all"><Truck size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- TAB CONTENT: GRN LOGS --- */}
            {activeTab === 'grn' && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <tr><th className="p-8">Date</th><th className="p-8">Material</th><th className="p-8">Vendor/Source</th><th className="p-8 text-right">Qty Received</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 font-bold">
                            {logs.filter(l => l.type === 'in').map((log, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                    <td className="p-8 text-xs text-slate-400">{new Date(log.created_at).toLocaleDateString()}</td>
                                    <td className="p-8 text-slate-900 uppercase italic">{log.material_name}</td>
                                    <td className="p-8 text-slate-500 uppercase text-xs">{log.notes || 'Direct Purchase'}</td>
                                    <td className="p-8 text-right text-emerald-600 font-black">+ {log.quantity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- TAB CONTENT: WASTAGE REPORT --- */}
            {activeTab === 'wastage' && (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <tr><th className="p-8">Date</th><th className="p-8">Material</th><th className="p-8">Reason</th><th className="p-8 text-right">Loss Qty</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 font-bold text-sm">
                            {logs.filter(l => l.type === 'out').map((log, idx) => (
                                <tr key={idx} className="hover:bg-rose-50/50">
                                    <td className="p-8 text-xs text-slate-400">{new Date(log.created_at).toLocaleDateString()}</td>
                                    <td className="p-8 text-slate-900 uppercase italic">{log.material_name}</td>
                                    <td className="p-8"><span className="px-3 py-1 bg-rose-50 text-rose-600 rounded-lg text-[10px] uppercase font-black border border-rose-100">{log.notes || 'Damage'}</span></td>
                                    <td className="p-8 text-right text-rose-500 font-black">- {log.quantity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- ALL MODALS (Update, Transfer, Wastage, New Item) stay the same as previous version --- */}
            {/* ... Modal Logic ... */}

            {modalType && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleAction} className="bg-white w-full max-w-xl rounded-[48px] shadow-2xl p-12 space-y-8 animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl font-black italic tracking-tighter uppercase">
                                {modalType === 'transfer' ? 'Site Allocation' : modalType === 'wastage' ? 'Log Material Loss' : 'Update Stock'}
                            </h2>
                            <button type="button" onClick={() => setModalType(null)} className="p-2 bg-slate-100 rounded-full"><X /></button>
                        </div>
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Select Material</label>
                                <select required className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none" value={formData.inventory_id} onChange={e => setFormData({ ...formData, inventory_id: e.target.value })}>
                                    <option value="">Choose item...</option>
                                    {items.map(i => <option key={i.id} value={i.id}>{i.name} ({i.stock} {i.unit} available)</option>)}
                                </select>
                            </div>
                            {modalType === 'transfer' && (
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Project Site</label>
                                    <select required className="w-full p-5 bg-indigo-50 border border-indigo-100 rounded-3xl font-black text-indigo-600 outline-none" value={formData.project_id} onChange={e => setFormData({ ...formData, project_id: e.target.value })}>
                                        <option value="">Select Site...</option>
                                        {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                    </select>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Quantity</label>
                                    <input required type="number" step="0.01" className="w-full p-5 bg-slate-50 rounded-3xl font-black text-xl" placeholder="0.00" value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: e.target.value })} />
                                </div>
                                {modalType === 'update' && (
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Type</label>
                                        <select className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-xs uppercase" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                                            <option value="in">Inflow (Purchase)</option>
                                            <option value="out">Outflow (Consumption)</option>
                                        </select>
                                    </div>
                                )}
                                {modalType === 'wastage' && (
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Reason</label>
                                        <select className="w-full p-5 bg-rose-50 rounded-3xl font-bold text-xs uppercase" value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })}>
                                            <option value="Cutting Loss">Cutting Loss</option>
                                            <option value="Damage">Damage</option>
                                            <option value="Mistake">Mistake</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                            <textarea placeholder="Notes (Internal tracking info...)" className="w-full p-5 bg-slate-50 rounded-3xl font-medium text-sm h-24 outline-none" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
                            <button disabled={formLoading} className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl flex items-center justify-center gap-3 hover:bg-indigo-600 transition-all">
                                {formLoading ? <Loader2 className="animate-spin" /> : <CheckCircle />} Confirm Update
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {isNewItemModalOpen && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleAddNewItem} className="bg-white w-full max-w-lg rounded-[48px] shadow-2xl p-12 space-y-6 animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-black italic tracking-tighter uppercase">Master Register</h2><button type="button" onClick={() => setIsNewItemModalOpen(false)} className="p-2 bg-slate-100 rounded-full"><X /></button></div>
                        <div className="space-y-4">
                            <input required className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none" placeholder="Material Name" value={newItem.material_name} onChange={e => setNewItem({ ...newItem, material_name: e.target.value })} />
                            <div className="grid grid-cols-2 gap-4">
                                <select className="p-5 bg-slate-50 rounded-3xl font-bold" value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })}>
                                    <option value="Plywood">Plywood</option>
                                    <option value="Laminate">Laminate</option>
                                    <option value="Hardware">Hardware</option>
                                    <option value="Veneer">Veneer</option>
                                </select>
                                <select className="p-5 bg-slate-50 rounded-3xl font-bold" value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })}>
                                    <option value="Sheets">Sheets</option>
                                    <option value="Pairs">Pairs</option>
                                    <option value="Boxes">Boxes</option>
                                    <option value="Rft">Rft</option>
                                </select>
                            </div>
                            <button disabled={formLoading} className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-lg shadow-2xl flex items-center justify-center gap-3">{formLoading ? <Loader2 className="animate-spin" /> : <CheckCircle />} Register Material</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default InventoryModule;